import { useEffect, useRef } from 'react';
import { useLocation } from 'wouter';
import { useConsent } from '@/context/ConsentContext';
import { browserGoogleMeasurement } from '@/lib/googleMeasurementBrowser';
import type { AnalyticsItem } from '@/lib/googleAnalytics';
import type { VerifiedPaymentStatus } from '@/lib/paymentState';

export function GoogleAdsMeasurement() {
  const { isReady, preferences } = useConsent();
  const [location] = useLocation();
  useEffect(() => {
    if (!isReady) return;
    let cancelled = false;
    const tracker = browserGoogleMeasurement();
    void tracker.updateConsent({ analytics: preferences?.analytics === true, marketing: preferences?.marketing === true }).then(() => {
      if (!cancelled) tracker.analytics.pageView();
    }).catch(() => { /* Optional measurement must not interrupt the storefront. */ });
    return () => { cancelled = true; };
  }, [isReady, preferences?.marketing, preferences?.analytics, location]);
  return null;
}

export function useGoogleAdsPurchase(paymentIntentId: string | null, status: VerifiedPaymentStatus | null) {
  const { isReady, preferences } = useConsent();
  useEffect(() => {
    if (!isReady || (!preferences?.marketing && !preferences?.analytics) || !status || !paymentIntentId) return;
    let cancelled = false;
    const tracker = browserGoogleMeasurement();
    void tracker.updateConsent({ analytics: preferences?.analytics === true, marketing: preferences?.marketing === true }).then(ready => {
      if (ready && !cancelled) {
        tracker.ads.trackPurchase(paymentIntentId, status);
        tracker.analytics.trackPurchase(paymentIntentId, status, tracker.readQuote(paymentIntentId));
      }
    }).catch(() => { /* Optional measurement must not interrupt the storefront. */ });
    return () => { cancelled = true; };
  }, [isReady, preferences?.marketing, preferences?.analytics, paymentIntentId, status]);
}

export function useGoogleAnalyticsEvent(name: 'view_item' | 'begin_checkout', items: AnalyticsItem[], eventKey: string) {
  const { isReady, preferences } = useConsent();
  const sent = useRef('');
  const serialized = JSON.stringify(items);
  useEffect(() => {
    if (!isReady || !preferences?.analytics || !eventKey || sent.current === eventKey) return;
    let cancelled = false;
    const tracker = browserGoogleMeasurement();
    void tracker.updateConsent({ analytics: true, marketing: preferences.marketing }).then(() => {
      if (!cancelled && tracker.analytics.ecommerce(name, JSON.parse(serialized))) sent.current = eventKey;
    }).catch(() => { /* Optional measurement must not interrupt the storefront. */ });
    return () => { cancelled = true; };
  }, [isReady, preferences?.analytics, preferences?.marketing, name, eventKey, serialized]);
}
